// Alpine component for the assistant voice-call overlay
// (resources/views/components/chats/voice-call-overlay.blade.php).
//
// Registered here in the bundle rather than in an @script block, for the same reason
// MessageInput is: an @script registration can be missing when Alpine re-initializes
// the tree after a morph or a wire:navigate, and x-data would then resolve to
// undefined and every expression in the overlay would throw.
//
// Recording reuses window.VoiceRecorder (resources/js/capacitor.js) unchanged — the
// same module the composer's mic button drives, including its MediaRecorder format
// negotiation and permission recovery.

// how long a turn may sit without any phase update before the overlay reconciles
// against the server. A broadcast can simply not arrive — a dropped socket, a worker
// killed between publishing and finishing — and without a clock of its own the
// overlay would count up forever against a turn nobody is processing. The agent job's
// own ceiling is 600s, so a long turn can outlive one window — that is fine:
// reconcile() re-arms while the server reports the turn still moving, and only fails
// when the server has nothing. Kept below the ceiling deliberately, so a dropped
// socket recovers in 5.5 min instead of waiting out the full budget.
const TURN_WATCHDOG_MS = 330000;

// server phases that mean the turn is over, one way or the other
const TERMINAL_PHASES = ['ready', 'failed'];

// failures that re-running synthesis on the same text cannot fix
const NON_RETRYABLE_ERRORS = ['answer_too_long', 'answer_not_saved'];

// Both providers read back noticeably slower than someone talking to you would, which
// on a call reads as the assistant being ponderous rather than careful. Applied at
// playback rather than at the provider: ElevenLabs caps its own speed setting at 1.2,
// OpenAI allows up to 4, and speech falls back between them — pacing the audio here is
// the one place that behaves identically whichever engine produced the file.
const SPEECH_RATE = 1.5;

function registerVoiceCall() {
    window.Alpine.data('VoiceCall', (config = {}) => {
        return {
            // ready | recording | uploading | processing | transcribed | synthesizing | speaking | failed
            state: 'ready',
            isRecording: false,
            elapsedSeconds: 0,
            elapsedTimer: null,
            recordingSeconds: 0,
            recordingTimer: null,
            audioLevel: 0,
            audioContext: null,
            levelMeterFrame: null,
            answer: null,
            errorCode: null,
            audioUrl: null,
            botMessageId: null,
            // the player is hidden, so a browser that refuses autoplay would otherwise be
            // a silent overlay with nothing to press. This is what surfaces the one control
            // that can rescue it
            autoplayBlocked: false,
            watchdogTimer: null,
            watchdogFallback: 'timed_out',
            // the highest phase sequence seen for the current turn. Queued broadcasts can
            // land out of order, and without this a late `processing` would visibly
            // un-finish a turn the caller has already heard
            lastSequence: 0,
            turnId: null,
            echoHandler: null,
            messages: config.messages ?? {},
            maxRecordingSeconds: config.maxRecordingSeconds ?? 300,

            audioPrimed: false,

            init() {
                this.subscribe();
                this.startElapsedTimer();
            },

            destroy() {
                this.teardown();
            },

            get elapsedDisplay() {
                const minutes = Math.floor(this.elapsedSeconds / 60);
                const seconds = this.elapsedSeconds % 60;

                return `${minutes}:${String(seconds).padStart(2, '0')}`;
            },

            // the states where the assistant owes an answer and the caller is waiting.
            // Gates both the disabled record button and the animated dots: `recording` is
            // excluded from it because that is the CALLER working, and it already has a
            // timer and a live level meter that a third animation would only compete with
            get isBusy() {
                return ['uploading', 'processing', 'transcribed', 'synthesizing'].includes(this.state);
            },

            get canRecord() {
                return this.state === 'ready' || this.state === 'failed';
            },

            get statusLabel() {
                return this.messages[this.state] ?? this.messages.ready ?? '';
            },

            get errorLabel() {
                if (!this.errorCode) {
                    return null;
                }

                // unknown codes fall back to the generic copy rather than rendering a raw
                // slug: the server is free to add codes this bundle has not shipped yet
                return this.messages.errors?.[this.errorCode] ?? this.messages.errors?.generic ?? null;
            },

            // retry re-runs synthesis on the SAME text, so a failure caused by that text is
            // not retryable — offering the button would just refuse again. The answer stays
            // on screen either way, which is the actual fallback
            get canRetryAudio() {
                return this.state === 'failed' && !!this.botMessageId && !NON_RETRYABLE_ERRORS.includes(this.errorCode);
            },

            subscribe() {
                if (typeof Echo === 'undefined' || typeof Echo.private !== 'function' || !config.userId) {
                    return;
                }

                this.echoHandler = payload => this.onTurnUpdated(payload?.model ?? {});

                Echo.private(`App.Models.User.${config.userId}`).listen(
                    '.AssistantVoiceCallTurnUpdated',
                    this.echoHandler
                );
            },

            onTurnUpdated(model) {
                // three filters, all required. The channel is per-user, so a second tab of
                // the same person receives this too (session), an earlier turn can still be
                // publishing after a newer one started (turn), and out-of-order delivery
                // must not walk the UI backwards (sequence).
                if (model.session_id !== config.sessionId || model.turn_id !== this.turnId) {
                    return;
                }

                if ((model.sequence ?? 0) <= this.lastSequence) {
                    return;
                }

                this.lastSequence = model.sequence ?? 0;
                this.armWatchdog();

                if (model.bot_message_id) {
                    this.botMessageId = model.bot_message_id;
                    this.loadAnswer(model.bot_message_id);
                }

                if (model.phase === 'failed') {
                    this.fail(model.error_code);

                    return;
                }

                if (model.phase === 'ready') {
                    // NOT cleared here: play() has its own round trip to resolve the audio
                    // URL, and dropping the clock before that returns is how a stuck resolve
                    // becomes a permanently silent overlay. onPlaybackEnded clears it
                    this.play(model.voice_message_id);

                    return;
                }

                // a non-terminal phase can still carry a cause worth showing — a failed
                // transcription the model answered around, say. Copying it only in the
                // terminal branch meant that message was localized and then never displayed
                this.errorCode = model.error_code ?? null;
                this.state = model.phase;
            },

            async toggleRecording() {
                // THE gesture to prime audio on. Opening the call is a wire:click, so
                // init() runs after a server round trip and is no longer inside a user
                // activation — priming there is refused and buys nothing. This handler is
                // a real tap, and it always precedes the first answer
                this.primeAudio();

                if (this.isRecording) {
                    await this.stopRecording();

                    return;
                }

                await this.startRecording();
            },

            async startRecording() {
                if (!this.canRecord || this.isRecording || !window.VoiceRecorder) {
                    return;
                }

                // a turn is in flight until its audio has played; starting a second one
                // would race two answers onto one line
                this.stopPlayback();

                try {
                    if (!(await window.VoiceRecorder.canRecord())) {
                        this.fail('device_unsupported');

                        return;
                    }

                    if (!(await window.VoiceRecorder.hasPermission())) {
                        if (!(await window.VoiceRecorder.requestPermission())) {
                            this.fail('mic_denied');

                            return;
                        }
                    }

                    await window.VoiceRecorder.startRecording();

                    this.isRecording = true;
                    this.state = 'recording';
                    this.errorCode = null;
                    this.answer = null;
                    this.audioUrl = null;
                    // otherwise a tap-to-hear button for the PREVIOUS answer stays on screen
                    // through the new recording
                    this.autoplayBlocked = false;
                    // cleared with the rest: left set, a new turn that fails before producing
                    // an answer would offer "read the answer aloud again" against the PREVIOUS
                    // turn's answer, and re-synthesize it
                    this.botMessageId = null;
                    // and the turn id with it. A stale one is worse than none: the watchdog
                    // would reconcile against the PREVIOUS turn, find it terminal and READY,
                    // and replay that answer as if it were this recording's
                    this.turnId = null;
                    this.lastSequence = 0;
                    this.startRecordingTimer();
                    this.startLevelMeter();
                } catch (error) {
                    console.error('Voice call recording error:', error);
                    this.isRecording = false;
                    this.stopRecordingTimer();
                    this.stopLevelMeter();
                    this.fail('recording_failed');
                }
            },

            async stopRecording() {
                if (!this.isRecording || !window.VoiceRecorder) {
                    return;
                }

                this.stopRecordingTimer();
                this.stopLevelMeter();

                try {
                    const file = await window.VoiceRecorder.stopRecording();

                    this.isRecording = false;

                    if (!file) {
                        this.fail('recording_failed');

                        return;
                    }

                    if (file.size > (config.audioSizeLimit ?? Infinity)) {
                        this.fail('recording_too_large');

                        return;
                    }

                    this.state = 'uploading';
                    // the Livewire upload can simply never call back — no finish event, no
                    // error event — and there is no turn on the server yet to reconcile
                    // against, so this window needs a clock of its own
                    this.armWatchdog('upload_failed');
                    this.upload(file);
                } catch (error) {
                    console.error('Voice call stop error:', error);
                    this.isRecording = false;
                    this.fail('recording_failed');
                }
            },

            upload(file) {
                // routed through the composer's own hidden voice input, so the file travels
                // the identical Livewire upload path (s3-private temp disk, server-side
                // type and size validation) rather than a second one written for calls
                const input = this.$refs.voiceInput;

                if (!input) {
                    this.fail('upload_failed');

                    return;
                }

                const transfer = new DataTransfer();
                transfer.items.add(file);
                input.files = transfer.files;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            },

            // called from the blade on livewire-upload-finish for the voice input
            async onUploadFinished() {
                if (this.state !== 'uploading') {
                    return;
                }

                // armed BEFORE the await, not after. A $wire promise in this codebase does
                // not settle when the request itself fails — the catch below never runs, and
                // an await that never returns would leave the overlay in `uploading` forever
                // with no clock on it. The watchdog reconciles against server state instead.
                this.armWatchdog();

                try {
                    const accepted = await this.$wire.call('sendVoiceCallTurn');

                    // read AFTER the round trip: the turn id is issued server-side, and the
                    // echo filter above is worthless until this component knows it
                    this.turnId = this.$wire.get('voiceCallTurnId');
                    this.lastSequence = 0;

                    // the server rejected it (invalid or oversized upload, a storage failure,
                    // a lock). It has already published the terminal phase, but this client
                    // may never have learned the turn id in time to receive it, so read the
                    // outcome back rather than sitting in `processing`
                    if (accepted === false) {
                        // the server clears its turn id on a rejection, so turnId is null
                        // here unless a turn really was created and then failed — in which
                        // case reconcile reports that turn's own cause instead of this
                        this.watchdogFallback = 'turn_not_queued';
                        await this.reconcile();

                        return;
                    }

                    this.state = 'processing';
                    this.armWatchdog();
                } catch (error) {
                    console.error('Voice call submit error:', error);
                    this.fail('upload_failed');
                }
            },

            onUploadError() {
                this.fail('upload_failed');
            },

            async loadAnswer(messageId) {
                if (this.answer) {
                    return;
                }

                this.answer = await this.messageText(messageId, 'message');
            },

            async messageText(messageId, field) {
                try {
                    return await this.$wire.call('voiceCallMessageText', messageId, field);
                } catch (error) {
                    console.error('Voice call caption error:', error);

                    return null;
                }
            },

            async play(voiceMessageId) {
                if (!voiceMessageId) {
                    // the answer exists but has no audio — surfaced as a failure the caller
                    // can retry, with the text still on screen
                    this.fail('tts_failed');

                    return;
                }

                try {
                    const url = await this.$wire.call('voiceCallAudioUrl', voiceMessageId);

                    if (!url) {
                        this.fail('tts_failed');

                        return;
                    }

                    this.audioUrl = url;
                    this.state = 'speaking';

                    // the watchdog stays armed until playback actually ENDS, not until it
                    // is handed off. A media error mid-playback fires no rejection and no
                    // `ended` — without a clock still running, the overlay would sit on
                    // `speaking` forever with no way to record again
                    this.armWatchdog('tts_failed');

                    this.$nextTick(() => this.startPlayback());
                } catch (error) {
                    console.error('Voice call playback error:', error);
                    this.fail('tts_failed');
                }
            },

            startPlayback() {
                const audio = this.$refs.player;

                if (!audio || !this.audioUrl) {
                    return;
                }

                if (audio.src !== this.audioUrl) {
                    audio.src = this.audioUrl;
                }

                // set on every attempt, not once at init: assigning src resets the rate on
                // some engines, and preservesPitch keeps 1.5x sounding like the same voice
                // talking faster rather than a chipmunk (Safari needs it named explicitly)
                audio.preservesPitch = true;
                audio.playbackRate = SPEECH_RATE;

                const attempt = audio.play();

                if (!attempt || typeof attempt.catch !== 'function') {
                    this.autoplayBlocked = false;

                    return;
                }

                attempt
                    .then(() => {
                        this.autoplayBlocked = false;
                    })
                    .catch(error => {
                        // NotAllowedError is the ONLY rejection a tap can fix: the audio
                        // exists and is reachable, the browser just wants a gesture. Treating
                        // every rejection that way was a dead end — a NotSupportedError
                        // (codec the browser will not decode) showed a button that retried
                        // the same broken file forever, with the state stuck on `speaking`
                        // and `canRecord` false, so the call could not continue at all.
                        if (error?.name === 'NotAllowedError') {
                            console.warn('Voice call autoplay blocked:', error);
                            this.autoplayBlocked = true;
                            this.state = 'speaking';

                            return;
                        }

                        console.error('Voice call playback error:', error);
                        this.fail('tts_failed');
                    });
            },

            onPlaybackEnded() {
                this.clearWatchdog();
                this.autoplayBlocked = false;
                this.state = 'ready';
            },

            // a media error after playback started produces no rejection and no `ended`,
            // so this is the only signal that the answer will never be heard
            onPlaybackError() {
                console.error('Voice call media error');
                this.fail('tts_failed');
            },

            stopPlayback() {
                const audio = this.$refs.player;

                if (audio) {
                    audio.pause();
                }
            },

            // Unlock the element from inside a real user gesture so the later programmatic
            // play() is not the page's first audio action. Uses a silent data-URI source
            // because play() on a source-less element rejects immediately and unlocks
            // nothing. Best-effort: if it is refused the turn still succeeds, the answer is
            // on screen and the native controls give the caller a Play button.
            primeAudio() {
                const audio = this.$refs.player;

                if (!audio || this.audioPrimed) {
                    return;
                }

                this.audioPrimed = true;

                try {
                    audio.muted = true;
                    audio.src =
                        'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tAwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAADAAABgAA=';

                    const attempt = audio.play();

                    if (attempt && typeof attempt.catch === 'function') {
                        attempt
                            .then(() => {
                                audio.pause();
                                audio.currentTime = 0;
                                audio.muted = false;
                            })
                            .catch(() => {
                                audio.muted = false;
                            });

                        return;
                    }

                    audio.muted = false;
                } catch (error) {
                    audio.muted = false;
                }
            },

            async retryAudio() {
                if (!this.botMessageId) {
                    return;
                }

                this.errorCode = null;
                this.state = 'synthesizing';

                // armed before the await for the same reason as the upload: a failed
                // Livewire request never settles its promise here
                this.armWatchdog();

                try {
                    await this.$wire.call('retryVoiceCallAudio', this.botMessageId);

                    this.turnId = this.$wire.get('voiceCallTurnId');
                    this.lastSequence = 0;
                    this.armWatchdog();
                } catch (error) {
                    console.error('Voice call retry error:', error);
                    this.fail('tts_failed');
                }
            },

            fail(errorCode) {
                this.clearWatchdog();
                this.stopRecordingTimer();
                this.stopLevelMeter();
                this.isRecording = false;
                this.errorCode = errorCode ?? 'generic';
                this.state = 'failed';
            },

            // the recovery half of the watchdog: before giving up, ask the server what it
            // recorded. A turn that finished while the socket was down reports `ready`
            // here and the call continues instead of showing a false failure
            async reconcile() {
                if (!this.turnId) {
                    this.fail(this.watchdogFallback);

                    return;
                }

                try {
                    const state = await this.$wire.call('voiceCallTurnState', this.turnId);

                    if (state && TERMINAL_PHASES.includes(state.phase)) {
                        // replay it through the normal handler, past the sequence filter:
                        // this is a recovery, so whatever the server recorded wins
                        this.lastSequence = 0;
                        this.onTurnUpdated({ ...state, turn_id: this.turnId, session_id: config.sessionId });

                        return;
                    }

                    // still moving, just out of touch — a dropped socket, not a dead turn.
                    // Adopt the server's phase and give it another window rather than
                    // reporting a timeout over work that is still running
                    if (state) {
                        this.lastSequence = 0;
                        this.onTurnUpdated({ ...state, turn_id: this.turnId, session_id: config.sessionId });
                        this.armWatchdog();

                        return;
                    }
                } catch (error) {
                    console.error('Voice call reconcile error:', error);
                }

                this.fail(this.watchdogFallback);
            },

            // fallbackCode is what the turn fails with when there is nothing on the server
            // to reconcile against — before a turn id exists, the honest cause is whatever
            // stage we are stuck in, not a generic timeout
            armWatchdog(fallbackCode = 'timed_out') {
                this.clearWatchdog();
                this.watchdogFallback = fallbackCode;
                this.watchdogTimer = setTimeout(() => this.reconcile(), TURN_WATCHDOG_MS);
            },

            clearWatchdog() {
                if (this.watchdogTimer) {
                    clearTimeout(this.watchdogTimer);
                    this.watchdogTimer = null;
                }
            },

            startElapsedTimer() {
                this.elapsedSeconds = 0;
                this.elapsedTimer = setInterval(() => {
                    this.elapsedSeconds++;
                }, 1000);
            },

            startRecordingTimer() {
                this.recordingSeconds = 0;
                this.recordingTimer = setInterval(() => {
                    this.recordingSeconds++;

                    if (this.recordingSeconds >= this.maxRecordingSeconds) {
                        this.stopRecording();
                    }
                }, 1000);
            },

            stopRecordingTimer() {
                if (this.recordingTimer) {
                    clearInterval(this.recordingTimer);
                    this.recordingTimer = null;
                }
            },

            // same analyser the composer's mic meter uses (resources/js/message-input.js).
            // Best-effort by design: the native recorder exposes no stream, and recording
            // works perfectly well without a moving bar
            startLevelMeter() {
                const stream = window.VoiceRecorder?.getActiveStream?.();

                if (!stream || !window.AudioContext) {
                    return;
                }

                try {
                    this.audioContext = new AudioContext();
                    const source = this.audioContext.createMediaStreamSource(stream);
                    const analyser = this.audioContext.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);
                    const data = new Uint8Array(analyser.frequencyBinCount);

                    const tick = () => {
                        if (!this.isRecording) {
                            return;
                        }

                        analyser.getByteTimeDomainData(data);
                        let sum = 0;
                        for (let i = 0; i < data.length; i++) {
                            const value = (data[i] - 128) / 128;
                            sum += value * value;
                        }
                        this.audioLevel = Math.min(100, Math.round(Math.sqrt(sum / data.length) * 300));
                        this.levelMeterFrame = requestAnimationFrame(tick);
                    };

                    this.levelMeterFrame = requestAnimationFrame(tick);
                } catch (error) {
                    // the level meter is best-effort; recording works without it
                }
            },

            stopLevelMeter() {
                if (this.levelMeterFrame) {
                    cancelAnimationFrame(this.levelMeterFrame);
                    this.levelMeterFrame = null;
                }

                if (this.audioContext) {
                    this.audioContext.close().catch(() => {});
                    this.audioContext = null;
                }

                this.audioLevel = 0;
            },

            async hangUp() {
                // release the microphone first: whatever happens to the request below, the
                // recording indicator must not outlive the caller's decision to hang up
                if (this.isRecording && window.VoiceRecorder) {
                    try {
                        await window.VoiceRecorder.cancelRecording();
                    } catch (error) {
                        console.error('Voice call cancel error:', error);
                    }
                }

                this.isRecording = false;
                this.stopRecordingTimer();
                this.stopLevelMeter();
                this.stopPlayback();
                this.clearWatchdog();

                // an already-submitted turn is NOT cancelled here — its tools may have
                // written already, so it finishes and lands in the thread like any other
                // message. Hanging up only stops this overlay listening.
                //
                // The server call is what removes the overlay, and teardown runs only once
                // it has: tearing down first would, on a failed request, leave a visible
                // overlay with no Echo subscription and no timers — one that cannot be
                // dismissed and cannot report anything either
                try {
                    await this.$wire.call('endVoiceCall');
                    this.teardown();
                } catch (error) {
                    console.error('Voice call hang-up error:', error);
                }
            },

            teardown() {
                this.clearWatchdog();
                this.stopRecordingTimer();
                this.stopLevelMeter();
                this.stopPlayback();

                if (this.elapsedTimer) {
                    clearInterval(this.elapsedTimer);
                    this.elapsedTimer = null;
                }

                if (
                    this.echoHandler &&
                    typeof Echo !== 'undefined' &&
                    typeof Echo.private === 'function' &&
                    config.userId
                ) {
                    Echo.private(`App.Models.User.${config.userId}`).stopListening(
                        '.AssistantVoiceCallTurnUpdated',
                        this.echoHandler
                    );

                    this.echoHandler = null;
                }
            },
        };
    });
}

// module scripts run before Livewire starts Alpine, so alpine:init is the normal
// path; the direct call covers the edge where Alpine is already up when this loads
document.addEventListener('alpine:init', registerVoiceCall);

if (window.Alpine) {
    registerVoiceCall();
}
